import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { CircularProgress, Typography } from "@mui/material";
import { Box } from "@mui/system";
import CreateNewModal from "../Modals/CreateNewModal.js";

const CreateAutoComplete = ({
  getOptionLabel,
  options,
  value,
  handleNew,
  handleSelect,
  handleClear,
  label,
  isOptionEqualToFunc,
  tableView,
  modalView,
  useCreateModal,
  modalHeader,
  modalText,
  modalIcon,
  type,
  error,
  disabled,
  loading,
}) => {
  const [currentInput, setCurrentInput] = useState("");
  const [createNewModalOpen, setCreateNewModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState({});

  const handleCurrentInputUpdate = (e) => {
    if (e) {
      setCurrentInput(e.target?.value || "");
    } else {
      setCurrentInput("");
    }
  };

  const handleOnChange = (e, newValue) => {
    if (newValue === null) {
      handleClear();
    } else if (newValue.id === -1) {
      if (useCreateModal) {
        setModalValue(newValue);
        setCreateNewModalOpen(true);
      } else {
        handleNew(newValue);
      }
    } else {
      handleSelect(newValue);
      setCurrentInput("");
    }
  };

  const modalSubmit = () => {
    const temp = modalValue;
    setModalValue({});
    handleNew(temp);
    setCreateNewModalOpen(false);
  };

  const renderOption = (props, option) => {
    if (option.id === -1 && !option.name) {
      return null;
    }
    return (
      <Box
        component="li"
        {...props}
        sx={{
          backgroundColor: (theme) => `${theme.palette.pure.white} !important`,
          "&:hover": {
            backgroundColor: (theme) =>
              `${theme.palette.terrain[300]} !important`,
          },
        }}
        key={option.id || option.name}
      >
        <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
          <Box
            sx={{
              width: "50%",
              "& .Mui-focused": {
                color: (theme) => `${theme.palette.terrain[300]} !important`,
              },
            }}
          >
            <Typography variant="body1">{option.name}</Typography>
          </Box>
          {loading ? <CircularProgress /> : null}
          {option.id === -1 ? (
            <Box sx={{ marginLeft: "auto" }}>
              <Typography
                variant={"body2"}
                sx={{ color: (theme) => theme.palette.primary[800] }}
              >
                + Create New
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  };

  const optionComparer = (o, v) => {
    if (!v) {
      return false;
    }
    return o.name?.toLowerCase()?.includes(v.name.toLowerCase()) || o.id === -1;
  };
  return (
    <>
      <Autocomplete
        size="small"
        getOptionLabel={getOptionLabel}
        onChange={handleOnChange}
        sx={{
          // in a table the field fills its cell, so the chain from here down needs a height for
          // the inner 100% to resolve against. Left alone outside table usage.
          ...(tableView
            ? {
                height: "100%",
                "& .MuiAutocomplete-inputRoot": { height: "100%" },
              }
            : {}),
          "& .MuiAutocomplete-option.Mui-focused": {
            backgroundColor: (theme) =>
              `${theme.palette.terrain[300]} !!important`,
          },
        }}
        options={[...options, { id: -1, name: currentInput || "" }]}
        isOptionEqualToValue={isOptionEqualToFunc || optionComparer}
        onInputChange={handleCurrentInputUpdate}
        onBlur={() => setCurrentInput("")}
        disabled={disabled}
        renderInput={(params) => {
          if (tableView)
            return (
              <TextField
                variant={type === "outlined" ? "outlined" : "filled"}
                error={error}
                hiddenLabel
                sx={{
                  // transparent so the table cell's own hover highlight shows through the whole
                  // cell - rows here can be taller than a single input
                  height: "100%",
                  "& .MuiFilledInput-root": {
                    background: "transparent",
                    height: "100%",
                    alignItems: "center",
                    paddingBottom: 0,
                    paddingTop: 0,
                    marginBottom: "0.5px",
                    "&:hover": {
                      background: "transparent",
                    },
                  },
                }}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  error: error,
                }}
              />
            );
          else
            return (
              <TextField
                {...params}
                label={label}
                error={error}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: (theme) =>
                      modalView === true && theme.palette.pure.white,
                  },
                }}
              />
            );
        }}
        renderOption={renderOption}
        value={value}
      />
      <CreateNewModal
        modalIcon={modalIcon}
        modalHeader={modalHeader}
        modalText={modalText}
        isOpen={createNewModalOpen}
        modalSubmit={modalSubmit}
        toggleModalOpen={setCreateNewModalOpen}
      />
    </>
  );
};

export default CreateAutoComplete;
